import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingCart, DollarSign, AlertTriangle, MessageSquare, Package, TrendingUp,
  ChefHat, FileText, Users, CreditCard, Receipt, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

// Role-based dashboard configuration
const ROLE_DASHBOARD_CONFIG = {
  super_admin: {
    title: 'Admin Dashboard',
    subtitle: 'Full system overview - all metrics and data',
    stats: ['orders', 'revenue', 'pending', 'food_safety', 'messages', 'low_stock'],
    sections: ['recent_orders', 'financial_summary', 'prep_summary']
  },
  operations_manager: {
    title: 'Operations Dashboard',
    subtitle: 'Orders, prep status, and stock levels',
    stats: ['orders', 'pending', 'preparing', 'low_stock'],
    sections: ['recent_orders', 'prep_summary']
  },
  kitchen: {
    title: 'Kitchen Dashboard',
    subtitle: 'Daily prep sheets and quantities',
    stats: ['prep_items', 'pending_prep', 'completed_prep'],
    sections: ['prep_summary']
  },
  customer_support: {
    title: 'Support Dashboard',
    subtitle: 'Customer messages and food safety cases',
    stats: ['messages', 'food_safety', 'open_cases'],
    sections: ['messages_summary', 'food_safety_summary']
  },
  finance: {
    title: 'Finance Dashboard',
    subtitle: 'Revenue, payments, and financial reports',
    stats: ['revenue', 'orders_today', 'paystack_fees', 'net_revenue'],
    sections: ['financial_summary', 'recent_orders_readonly']
  }
};

export default function AdminDashboard() {
  const { token, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [prepData, setPrepData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [menuCount, setMenuCount] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const userRole = user?.role || 'customer';
  const dashboardConfig = ROLE_DASHBOARD_CONFIG[userRole] || ROLE_DASHBOARD_CONFIG.operations_manager;

  useEffect(() => {
    fetchData();
    // Check menu count for production setup banner
    if (userRole === 'super_admin') {
      fetch(`${API_URL}/api/menu`)
        .then(r => r.ok ? r.json() : [])
        .then(items => setMenuCount(Array.isArray(items) ? items.length : 0))
        .catch(() => {});
    }
  }, [token, userRole]);

  const fetchData = async () => {
    try {
      const fetchPromises = [];
      
      // Summary - accessible to most roles
      if (['super_admin', 'operations_manager', 'finance'].includes(userRole)) {
        fetchPromises.push(
          fetch(`${API_URL}/api/admin/reports/summary`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          }).then(r => r.ok ? r.json() : null)
        );
      } else {
        fetchPromises.push(Promise.resolve(null));
      }

      // Orders - accessible to super_admin, operations, finance (read-only)
      if (['super_admin', 'operations_manager', 'finance'].includes(userRole)) {
        fetchPromises.push(
          fetch(`${API_URL}/api/admin/orders`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          }).then(r => r.ok ? r.json() : [])
        );
      } else {
        fetchPromises.push(Promise.resolve([]));
      }

      // Kitchen prep - accessible to super_admin, operations, kitchen
      if (['super_admin', 'operations_manager', 'kitchen'].includes(userRole)) {
        fetchPromises.push(
          fetch(`${API_URL}/api/kitchen/prep/today`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          }).then(r => r.ok ? r.json() : null)
        );
      } else {
        fetchPromises.push(Promise.resolve(null));
      }

      const [summaryData, ordersData, prepDataRes] = await Promise.all(fetchPromises);
      
      setSummary(summaryData);
      setRecentOrders(ordersData?.slice?.(0, 5) || []);
      setPrepData(prepDataRes);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Build stats based on role
  const getStatsForRole = () => {
    const allStats = {
      orders: { 
        title: 'Total Orders', 
        value: summary?.orders?.total || 0, 
        icon: ShoppingCart, 
        color: 'text-blue-600',
        bg: 'bg-blue-100',
        link: '/admin/orders'
      },
      pending: { 
        title: 'Pending Orders', 
        value: summary?.orders?.pending || 0, 
        icon: Package, 
        color: 'text-orange-600',
        bg: 'bg-orange-100',
        link: '/admin/orders?status=pending'
      },
      preparing: { 
        title: 'Preparing', 
        value: summary?.orders?.preparing || 0, 
        icon: ChefHat, 
        color: 'text-amber-600',
        bg: 'bg-amber-100',
        link: '/admin/orders?status=preparing'
      },
      revenue: { 
        title: 'Total Revenue', 
        value: `R${(summary?.revenue?.total || 0).toLocaleString()}`, 
        icon: DollarSign, 
        color: 'text-green-600',
        bg: 'bg-green-100',
        link: '/admin/reports'
      },
      orders_today: { 
        title: "Today's Orders", 
        value: summary?.orders?.today || 0, 
        icon: ShoppingCart, 
        color: 'text-blue-600',
        bg: 'bg-blue-100',
        link: '/admin/orders'
      },
      paystack_fees: { 
        title: 'Payment Fees', 
        value: `R${(summary?.revenue?.fees || 0).toLocaleString()}`, 
        icon: CreditCard, 
        color: 'text-red-600',
        bg: 'bg-red-100',
        link: '/admin/reports'
      },
      net_revenue: { 
        title: 'Net Revenue', 
        value: `R${((summary?.revenue?.total || 0) - (summary?.revenue?.fees || 0)).toLocaleString()}`, 
        icon: Receipt, 
        color: 'text-green-600',
        bg: 'bg-green-100',
        link: '/admin/reports'
      },
      food_safety: { 
        title: 'Food Safety Cases', 
        value: summary?.food_safety?.open_cases || 0, 
        icon: AlertTriangle, 
        color: 'text-red-600',
        bg: 'bg-red-100',
        link: '/admin/food-safety'
      },
      open_cases: { 
        title: 'Open Cases', 
        value: summary?.food_safety?.open_cases || 0, 
        icon: AlertTriangle, 
        color: 'text-red-600',
        bg: 'bg-red-100',
        link: '/admin/food-safety'
      },
      messages: { 
        title: 'Unread Messages', 
        value: summary?.messages?.unread || 0, 
        icon: MessageSquare, 
        color: 'text-purple-600',
        bg: 'bg-purple-100',
        link: '/admin/chat'
      },
      low_stock: { 
        title: 'Low Stock Items', 
        value: summary?.stock?.low_stock_items || 0, 
        icon: Package, 
        color: 'text-amber-600',
        bg: 'bg-amber-100',
        link: '/admin/stock'
      },
      prep_items: { 
        title: "Today's Prep Items", 
        value: prepData?.items?.length || 0, 
        icon: ChefHat, 
        color: 'text-orange-600',
        bg: 'bg-orange-100',
        link: '/admin/kitchen'
      },
      pending_prep: { 
        title: 'Pending Prep', 
        value: prepData?.items?.filter(i => i.prep_status === 'pending').length || 0, 
        icon: Package, 
        color: 'text-amber-600',
        bg: 'bg-amber-100',
        link: '/admin/kitchen'
      },
      completed_prep: { 
        title: 'Completed Prep', 
        value: prepData?.items?.filter(i => i.prep_status === 'completed').length || 0, 
        icon: Shield, 
        color: 'text-green-600',
        bg: 'bg-green-100',
        link: '/admin/kitchen'
      }
    };

    return dashboardConfig.stats.map(key => allStats[key]).filter(Boolean);
  };

  const stats = getStatsForRole();

  // One-click seed for production setup
  const handleSeedMenu = async () => {
    if (!confirm('Seed the database with the full Rosond menu (52 items + 6 sides + delivery addresses)? This is safe — it will not overwrite existing items.')) return;
    setSeeding(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/menu/seed-full-menu?force=false`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await r.json();
      if (r.ok) {
        setMenuCount(52);
        alert(`✅ ${data.message}`);
      } else {
        alert(`❌ ${data.detail || 'Seed failed'}`);
      }
    } catch (e) {
      alert('Failed to seed menu. Check your connection.');
    } finally {
      setSeeding(false);
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      created: 'bg-gray-100 text-gray-800',
      payment_pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      preparing: 'bg-blue-100 text-blue-800',
      ready_for_collection: 'bg-purple-100 text-purple-800',
      collected: 'bg-emerald-100 text-emerald-800',
      cancelled: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      ready: 'bg-purple-100 text-purple-800',
      delivered: 'bg-emerald-100 text-emerald-800'
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
  };

  const canSeeOrderValues = ['super_admin', 'operations_manager', 'finance'].includes(userRole);
  const canSeeOrders = ['super_admin', 'operations_manager', 'finance'].includes(userRole);
  const canSeePrep = ['super_admin', 'operations_manager', 'kitchen'].includes(userRole);

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="font-heading text-3xl font-semibold text-slate-900">{dashboardConfig.title}</h1>
          <Badge className="bg-amber-100 text-amber-800">{user?.role?.replace('_', ' ').toUpperCase()}</Badge>
        </div>
        <p className="text-slate-500">{dashboardConfig.subtitle}</p>
      </div>

      {/* Production Setup Banner — shown when menu is empty on super_admin */}
      {userRole === 'super_admin' && menuCount === 0 && (
        <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-300 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-bold text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Production Setup Required
            </p>
            <p className="text-sm text-amber-700 mt-1">
              Your menu is empty. Seed the database with the full Rosond menu (52 items, 6 sides, delivery addresses) to go live.
            </p>
          </div>
          <button
            onClick={handleSeedMenu}
            disabled={seeding}
            className="flex-shrink-0 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="seed-menu-dashboard-btn"
          >
            {seeding ? '⏳ Seeding...' : '🚀 Seed Menu Now'}
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {stats.map((stat, idx) => (
          <Link key={stat.title} to={stat.link}>
            <Card className="hover:border-primary/50 transition-colors cursor-pointer" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bg}`}>
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Kitchen Prep Summary - for kitchen, operations, super_admin */}
      {canSeePrep && prepData && (
        <Card className="mb-8" data-testid="prep-summary">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading flex items-center gap-2">
              <ChefHat className="h-5 w-5 text-orange-500" />
              Today&apos;s Prep ({prepData.day_of_week})
            </CardTitle>
            <Link to="/admin/kitchen" className="text-sm text-primary hover:underline">
              View Kitchen
            </Link>
          </CardHeader>
          <CardContent>
            {prepData.items?.length > 0 ? (
              <div className="space-y-3">
                {prepData.items.slice(0, 5).map(item => (
                  <div key={item.meal_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.name} className="w-10 h-10 rounded object-cover" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">{item.name}</p>
                        <p className="text-sm text-slate-500">{item.quantity_ordered} portions</p>
                      </div>
                    </div>
                    <Badge className={
                      item.prep_status === 'completed' ? 'bg-green-100 text-green-800' :
                      item.prep_status === 'started' ? 'bg-blue-100 text-blue-800' :
                      'bg-amber-100 text-amber-800'
                    }>
                      {item.prep_status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No prep items for today</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Orders - for super_admin, operations, finance (read-only) */}
      {canSeeOrders && (
        <Card data-testid="recent-orders">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-blue-500" />
              Recent Orders
              {userRole === 'finance' && <Badge className="bg-slate-100 text-slate-600 ml-2">Read Only</Badge>}
            </CardTitle>
            <Link to="/admin/orders" className="text-sm text-primary hover:underline">
              View All
            </Link>
          </CardHeader>
          <CardContent>
            {recentOrders.length > 0 ? (
              <div className="space-y-4">
                {recentOrders.map(order => (
                  <div key={order.order_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-slate-900">{order.customer_name}</p>
                      <p className="text-sm text-slate-500">
                        {order.items?.length || 0} items 
                        {canSeeOrderValues && ` • R${order.total_amount?.toFixed(2)}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className={getStatusClass(order.status)}>
                        {order.status?.toUpperCase().replace('_', ' ')}
                      </Badge>
                      <p className="text-xs text-slate-500 mt-1">{order.delivery_date}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No recent orders</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Kitchen-only message */}
      {userRole === 'kitchen' && !prepData?.items?.length && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-8 text-center">
            <ChefHat className="h-12 w-12 text-orange-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-orange-800 mb-2">No Prep Required Today</h3>
            <p className="text-orange-600">Check back later for today&apos;s prep list.</p>
          </CardContent>
        </Card>
      )}

      {/* Customer Support message */}
      {userRole === 'customer_support' && (
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-purple-500" />
                Messages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Link to="/admin/chat" className="text-primary hover:underline">
                View all customer messages →
              </Link>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Food Safety
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Link to="/admin/food-safety" className="text-primary hover:underline">
                View food safety cases →
              </Link>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
